// Private, immutable asset blobs. Scoped to the signed-in user, bounded and cleared on sign-out.
const CACHE = 'vizu-listing-assets-v1'
const MAX_BYTES = 80 * 1024 * 1024
const MAX_ITEMS = 100
let epoch = 0
const pending = new Map()
const keyFor = (owner, draft, asset) => new URL(`/__listing-assets/${encodeURIComponent(owner)}/${draft}/${asset}`, window.location.origin).href

export async function clearListingAssetCache() {
  epoch += 1
  pending.clear()
  try { await globalThis.caches?.delete(CACHE) } catch { /* Browser storage may be unavailable. */ }
}

export async function rememberListingAsset(owner, draft, asset, blob, version = epoch) {
  if (owner == null || !blob.type.startsWith('image/') || blob.size > MAX_BYTES || version !== epoch) return
  try {
    const cache = await globalThis.caches?.open(CACHE)
    if (!cache || version !== epoch) return
    const key = keyFor(owner, draft, asset)
    await cache.put(key, new Response(blob, { headers: { 'Content-Type': blob.type, 'Content-Length': String(blob.size) } }))
    if (version !== epoch) { await cache.delete(key); return }
    const keys = await cache.keys()
    let bytes = 0, count = 0
    for (const request of keys.reverse()) {
      const response = await cache.match(request)
      bytes += Number(response?.headers.get('Content-Length') || MAX_BYTES)
      if (++count > MAX_ITEMS || bytes > MAX_BYTES) await cache.delete(request)
    }
  } catch { /* A full or disabled cache must not prevent uploads or previews. */ }
}

export async function cachedListingAsset(owner, draft, asset, fetchBlob) {
  if (owner == null) return fetchBlob()
  const key = keyFor(owner, draft, asset), version = epoch
  if (pending.has(key)) return pending.get(key)
  const work = (async () => {
    try {
      const cache = await globalThis.caches?.open(CACHE)
      const hit = await cache?.match(key)
      if (hit && version === epoch) return hit.blob()
    } catch { /* Fall back to the authenticated API. */ }
    const blob = await fetchBlob()
    await rememberListingAsset(owner, draft, asset, blob, version)
    return blob
  })()
  pending.set(key, work)
  try { return await work } finally { if (pending.get(key) === work) pending.delete(key) }
}

export async function forgetListingAsset(owner, draft, asset) {
  if (owner == null) return
  try { const cache = await globalThis.caches?.open(CACHE); await cache?.delete(keyFor(owner, draft, asset)) } catch { /* Optional cache. */ }
}
